<template>
  <div class="exam-upload">
    <div v-if="exams.length" class="exam-list">
      <div v-for="exam in exams" :key="exam.id" class="exam-item">
        <a
          v-b-tooltip.hover.top="exam.description"
          :href="exam.file_url"
          target="_blank"
          rel="noopener"
          class="exam-link"
        >
          {{ shortDescription(exam.description) || "Exame" }}
        </a>
        <button class="btn btn--ghost btn--sm" @click="removeExam(exam.id)">
          Remover
        </button>
      </div>
    </div>

    <div class="exam-upload__form">
      <label class="exam-upload__file-label">
        <span
          v-b-tooltip.hover.top="selectedFile ? selectedFile.name : ''"
          class="btn btn--outline btn--sm"
        >
          {{ selectedFile ? shortFileName(selectedFile.name) : "Anexar exame" }}
        </span>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          class="exam-upload__input"
          @change="handleFileSelect"
        />
      </label>
      <!-- Descrição + Enviar numa linha abaixo do arquivo anexado -->
      <div v-if="selectedFile" class="exam-upload__desc-row">
        <input
          v-model="description"
          type="text"
          class="exam-upload__desc"
          :class="{ 'exam-upload__desc--invalid': descriptionMissing }"
          placeholder="Descrição (obrigatória)"
          required
          @input="descriptionMissing = false"
        />
        <button
          class="btn btn--accent btn--sm"
          :disabled="uploading"
          @click="submitExam"
        >
          {{ uploading ? "Enviando…" : "Enviar" }}
        </button>
      </div>
    </div>

    <p v-if="error" class="exam-upload__error">{{ error }}</p>
  </div>
</template>

<script>
import { Appointments } from "../services/appointments";

const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const MAX_SIZE = 10 * 1024 * 1024;
// Descrição e nome do arquivo são cortados no card (o texto completo aparece no
// tooltip do Bootstrap Vue ao passar o mouse) pra não quebrar o layout da consulta.
const DESCRIPTION_MAX = 13;
const FILE_NAME_MAX = 8;

export default {
  name: "ExamUpload",

  props: {
    appointmentId: { type: [String, Number], required: true }
  },

  data() {
    return {
      exams: [],
      selectedFile: null,
      description: "",
      uploading: false,
      error: null,
      // Destaca o campo quando se tenta enviar sem descrição
      descriptionMissing: false
    };
  },

  mounted() {
    this.loadExams();
  },

  methods: {
    shorten(text, max) {
      if (!text) return text;
      return text.length > max ? `${text.slice(0, max)}...` : text;
    },
    shortDescription(text) {
      return this.shorten(text, DESCRIPTION_MAX);
    },
    shortFileName(text) {
      return this.shorten(text, FILE_NAME_MAX);
    },

    async loadExams() {
      try {
        const { data } = await Appointments.listExamAttachments(
          this.appointmentId
        );
        this.exams = data;
      } catch {
        // lista permanece vazia sem bloquear o card
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!ACCEPTED_TYPES.includes(file.type)) {
        this.error = "Tipo de arquivo inválido. Envie PDF, JPEG ou PNG.";
        event.target.value = "";
        return;
      }

      if (file.size > MAX_SIZE) {
        this.error = "Arquivo maior que 10 MB.";
        event.target.value = "";
        return;
      }

      this.error = null;
      this.selectedFile = file;
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    async submitExam() {
      if (!this.selectedFile) return;
      // Descrição obrigatória: é o que identifica o exame na listagem/busca.
      const description = this.description.trim();
      if (!description) {
        this.descriptionMissing = true;
        this.error = "Informe uma descrição para o exame.";
        return;
      }
      this.uploading = true;
      this.error = null;
      try {
        const data = await this.fileToBase64(this.selectedFile);
        await Appointments.uploadExamAttachment(this.appointmentId, {
          description,
          file: { data }
        });
        this.selectedFile = null;
        this.description = "";
        await this.loadExams();
      } catch {
        this.error = "Erro ao enviar o exame. Tente novamente.";
      } finally {
        this.uploading = false;
      }
    },

    async removeExam(examId) {
      try {
        await Appointments.deleteExamAttachment(this.appointmentId, examId);
        this.exams = this.exams.filter(e => e.id !== examId);
      } catch {
        this.error = "Erro ao remover o exame.";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.exam-upload {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.exam-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.exam-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  min-width: 0; // permite o item encolher dentro do card
}

.exam-link {
  font-size: var(--font-size-sm);
  color: var(--color-primary);
  text-decoration: none;
  // Descrição longa: uma linha só com "…" (texto completo no title/hover)
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    text-decoration: underline;
  }
}

.exam-upload__form {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
}

.exam-upload__desc-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.exam-upload__file-label {
  cursor: pointer;
  margin: 0;
}

.exam-upload__input {
  display: none;
}

.exam-upload__desc {
  font-size: var(--font-size-sm);
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-sm);
  outline: none;

  &:focus {
    border-color: var(--color-primary);
  }

  &--invalid {
    border-color: var(--color-danger);
  }
}

.exam-upload__error {
  font-size: var(--font-size-xs);
  color: var(--color-danger);
  margin: 0;
}
</style>
